import {
    DATE_FORMATTING_OPTIONS,
    TIMELINE_DATE_FORMATTING_OPTIONS,
} from '../../../../chartRenderer/dateFormattingOptions';
import ColorPicker from '../ColorPicker';
import TreeMapHierarchy from '../TreeMapHierarchy';
import s from './ChartSpecificOptions.module.scss';

const POLAR_VALUE_TYPES = [
    { value: 'line', label: 'Line' },
    { value: 'column', label: 'Column' },
];

const LAYOUT_ALGORITHMS = [
    { value: 'sliceAndDice', label: 'Slice and Dice' },
    { value: 'stripes', label: 'Stripes' },
    { value: 'squarified', label: 'Squarified' },
    { value: 'strip', label: 'Strip' },
];

const DATE_ORDERING_OPTIONS = [
    { value: 'asc', label: 'Ascending' },
    { value: 'desc', label: 'Descending' },
];

const STACKING_TYPE_OPTIONS = [
    { value: 'normal', label: 'Normal' },
    { value: 'percent', label: 'Percent' },
];

const HEATMAP_CATEGORY_ORDERING_OPTIONS = [
    { value: 'asc', label: 'Ascending' },
    { value: 'desc', label: 'Descending' },
    { value: 'none', label: 'None (original order)' },
];

const HEATMAP_SORTING_TYPE_OPTIONS = [
    { value: 'number', label: 'Sort numerically' },
    { value: 'string', label: 'Sort alphabetically' },
    { value: 'date', label: 'Sort as date' },
];

const HEATMAP_COLOR_SCHEME_OPTIONS = [
    { value: 'blue', label: 'Blue' },
    { value: 'red', label: 'Red' },
];

const ChartSpecificOptions = ({
    chartType,
    config,
    updateConfig,
    datasetFields,
    loadingFields,
    validationErrors = {},
}) => {
    if (!chartType) return null;

    const renderPolarOptions = () => (
        <div className={s.OptionsGroup}>
            <h3 className={s.GroupTitle}>Polar Options</h3>
            <div className={s.Fields}>
                <div className={s.Field}>
                    <label className={s.Label}>Chart Type</label>
                    <select
                        className={s.Select}
                        value={config.polarChartValueType || 'line'}
                        onChange={(e) =>
                            updateConfig({
                                polarChartValueType: e.target.value,
                            })
                        }>
                        {POLAR_VALUE_TYPES.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={s.CheckboxField}>
                    <label className={s.CheckboxLabel}>
                        <input
                            type="checkbox"
                            checked={config.enableLineSymbols || false}
                            onChange={(e) =>
                                updateConfig({
                                    enableLineSymbols: e.target.checked,
                                })
                            }
                        />
                        <span>Show data point symbols</span>
                    </label>
                </div>
            </div>
        </div>
    );

    const renderLineOptions = () => (
        <div className={s.OptionsGroup}>
            <h3 className={s.GroupTitle}>Line Options</h3>
            <div className={s.Fields}>
                <div className={s.CheckboxField}>
                    <label className={s.CheckboxLabel}>
                        <input
                            type="checkbox"
                            checked={config.enableLineSymbols || false}
                            onChange={(e) =>
                                updateConfig({
                                    enableLineSymbols: e.target.checked,
                                })
                            }
                        />
                        <span>Show data point symbols</span>
                    </label>
                </div>
            </div>
        </div>
    );

    const renderAreaOptions = () => (
        <div className={s.OptionsGroup}>
            <h3 className={s.GroupTitle}>Area Options</h3>
            <div className={s.Fields}>
                <div className={s.Field}>
                    <label className={s.Label}>Stacking Type</label>
                    <select
                        className={s.Select}
                        value={config.stackingType || 'normal'}
                        onChange={(e) =>
                            updateConfig({ stackingType: e.target.value })
                        }>
                        {STACKING_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={s.CheckboxField}>
                    <label className={s.CheckboxLabel}>
                        <input
                            type="checkbox"
                            checked={config.enableLineSymbols || false}
                            onChange={(e) =>
                                updateConfig({
                                    enableLineSymbols: e.target.checked,
                                })
                            }
                        />
                        <span>Show data point symbols</span>
                    </label>
                </div>
            </div>
        </div>
    );

    const renderTreeMapOptions = () => (
        <div className={s.OptionsGroup}>
            <h3 className={s.GroupTitle}>TreeMap Options</h3>
            <div className={s.Fields}>
                <div className={s.Field}>
                    <label className={s.Label}>Layout Algorithm</label>
                    <select
                        className={s.Select}
                        value={config.layoutAlgorithm || 'sliceAndDice'}
                        onChange={(e) =>
                            updateConfig({ layoutAlgorithm: e.target.value })
                        }>
                        {LAYOUT_ALGORITHMS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={s.Field}>
                    <label className={s.Label}>
                        Hierarchy Levels
                        {validationErrors.hierarchyLevels && (
                            <span className={s.ErrorText}>
                                {' '}
                                - {validationErrors.hierarchyLevels}
                            </span>
                        )}
                    </label>
                    <TreeMapHierarchy
                        levels={config.hierarchyLevels || []}
                        onChange={(levels) =>
                            updateConfig({ hierarchyLevels: levels })
                        }
                        datasetFields={datasetFields}
                        loadingFields={loadingFields}
                    />
                </div>
            </div>
        </div>
    );

    const renderTimelineOptions = () => (
        <div className={s.OptionsGroup}>
            <h3 className={s.GroupTitle}>Timeline Options</h3>
            <div className={s.Fields}>
                <div className={s.Field}>
                    <label className={s.Label}>Date Property</label>
                    <select
                        className={`${s.Select} ${validationErrors.timelineDateProperty ? s.Invalid : ''}`}
                        value={config.timelineDateProperty || ''}
                        onChange={(e) =>
                            updateConfig({
                                timelineDateProperty: e.target.value,
                            })
                        }
                        disabled={loadingFields}>
                        <option value="">
                            {loadingFields ? 'Loading...' : 'Select property'}
                        </option>
                        {datasetFields.map((field) => (
                            <option key={field} value={field}>
                                {field}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={s.Field}>
                    <label className={s.Label}>Title Property</label>
                    <select
                        className={`${s.Select} ${validationErrors.timelineTitleProperty ? s.Invalid : ''}`}
                        value={config.timelineTitleProperty || ''}
                        onChange={(e) =>
                            updateConfig({
                                timelineTitleProperty: e.target.value,
                            })
                        }
                        disabled={loadingFields}>
                        <option value="">
                            {loadingFields ? 'Loading...' : 'Select property'}
                        </option>
                        {datasetFields.map((field) => (
                            <option key={field} value={field}>
                                {field}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={s.Field}>
                    <label className={s.Label}>Description Property</label>
                    <select
                        className={s.Select}
                        value={config.timelineDescriptionProperty || ''}
                        onChange={(e) =>
                            updateConfig({
                                timelineDescriptionProperty: e.target.value,
                            })
                        }
                        disabled={loadingFields}>
                        <option value="">
                            {loadingFields
                                ? 'Loading...'
                                : 'Select property (optional)'}
                        </option>
                        {datasetFields.map((field) => (
                            <option key={field} value={field}>
                                {field}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={s.Field}>
                    <label className={s.Label}>Date Ordering</label>
                    <select
                        className={s.Select}
                        value={config.dateOrdering || 'asc'}
                        onChange={(e) =>
                            updateConfig({ dateOrdering: e.target.value })
                        }>
                        {DATE_ORDERING_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={s.Field}>
                    <label className={s.Label}>Date Formatting</label>
                    <select
                        className={s.Select}
                        value={config.dateFormatting || 'none'}
                        onChange={(e) =>
                            updateConfig({ dateFormatting: e.target.value })
                        }>
                        {TIMELINE_DATE_FORMATTING_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={s.CheckboxField}>
                    <label className={s.CheckboxLabel}>
                        <input
                            type="checkbox"
                            checked={config.spacePointsEvenly || false}
                            onChange={(e) =>
                                updateConfig({
                                    spacePointsEvenly: e.target.checked,
                                })
                            }
                        />
                        <span>Space points evenly</span>
                    </label>
                </div>

                <div className={s.Field}>
                    <label className={s.Label}>Point Color</label>
                    <div className={s.ColorOption}>
                        <label className={s.RadioLabel}>
                            <input
                                type="radio"
                                name="timelineColorMode"
                                checked={config.timelineColorMode !== 'custom'}
                                onChange={() =>
                                    updateConfig({ timelineColorMode: 'auto' })
                                }
                            />
                            <span>Auto</span>
                        </label>
                        <label className={s.RadioLabel}>
                            <input
                                type="radio"
                                name="timelineColorMode"
                                checked={config.timelineColorMode === 'custom'}
                                onChange={() =>
                                    updateConfig({
                                        timelineColorMode: 'custom',
                                    })
                                }
                            />
                            <span>Custom</span>
                        </label>
                    </div>
                    {config.timelineColorMode === 'custom' && (
                        <div className={s.ColorPickerWrapper}>
                            <ColorPicker
                                value={config.timelineColor || '#4FA0FF'}
                                onChange={(color) =>
                                    updateConfig({ timelineColor: color })
                                }
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderHeatmapOptions = () => {
        const yAxis = config.yAxis || {};
        const updateYAxis = (updates) =>
            updateConfig({ yAxis: { ...yAxis, ...updates } });

        return (
            <div className={s.OptionsGroup}>
                <h3 className={s.GroupTitle}>Heat Map Y-Axis</h3>
                <div className={s.Fields}>
                    <div className={s.Field}>
                        <label className={s.Label}>
                            Y-Axis Category Ordering
                        </label>
                        <select
                            className={s.Select}
                            value={yAxis.categoryOrdering || 'asc'}
                            onChange={(e) =>
                                updateYAxis({
                                    categoryOrdering: e.target.value,
                                })
                            }>
                            {HEATMAP_CATEGORY_ORDERING_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={s.Field}>
                        <label className={s.Label}>Y-Axis Sorting Type</label>
                        <select
                            className={s.Select}
                            value={yAxis.categorySortingType || 'string'}
                            onChange={(e) =>
                                updateYAxis({
                                    categorySortingType: e.target.value,
                                })
                            }>
                            {HEATMAP_SORTING_TYPE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={s.Field}>
                        <label className={s.Label}>
                            Y-Axis Label Formatting
                        </label>
                        <select
                            className={s.Select}
                            value={yAxis.labelFormatting || 'none'}
                            onChange={(e) =>
                                updateYAxis({ labelFormatting: e.target.value })
                            }>
                            {DATE_FORMATTING_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={s.Field}>
                        <label className={s.Label}>Color Scheme</label>
                        <select
                            className={s.Select}
                            value={config.colorScheme || 'blue'}
                            onChange={(e) =>
                                updateConfig({ colorScheme: e.target.value })
                            }>
                            {HEATMAP_COLOR_SCHEME_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
        );
    };

    switch (chartType) {
        case 'polar':
            return renderPolarOptions();
        case 'line':
            return renderLineOptions();
        case 'area':
            return renderAreaOptions();
        case 'mixed':
            return renderLineOptions();
        case 'treemap':
            return renderTreeMapOptions();
        case 'timeline':
            return renderTimelineOptions();
        case 'heatmap':
            return renderHeatmapOptions();
        default:
            return null;
    }
};

export default ChartSpecificOptions;
